import supabase from '../config/supabase';
import config from '../config/environment';
import type { Student } from '../types';
import type { CheckoutSuccessSnapshotData, CheckoutStudentPrefillData } from '../pages/Checkout/checkout.types';
import { handleServiceError } from '../utils/errorHandler';
import { sanitizePublicLinkError } from '../utils/publicLinkErrors';

export const SHARED_TOKEN_PURPOSES = {
  STUDENT: 'student_token',
  CHECKOUT_PLAN: 'checkout_plan',
  CHECKOUT_STUDENT_UPDATE: 'checkout_student_update',
  CHECKOUT_SUCCESS: 'checkout_success',
  CHECKOUT_AWAITING_PAYMENT: 'checkout_awaiting_payment',
} as const;

export type SharedTokenPurpose = typeof SHARED_TOKEN_PURPOSES[keyof typeof SHARED_TOKEN_PURPOSES];

interface SharedTokenEdgeResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface EncodeSharedTokenResult {
  purpose: string;
  payload: Record<string, unknown>;
  token: string;
}

export interface DecodeSharedTokenResult {
  purpose: string | null;
  payload: Record<string, unknown>;
  token: string;
  studentId?: number;
  student?: Student;
  planId?: number;
  checkoutPrefill?: CheckoutStudentPrefillData;
}

class SharedTokenServiceClass {
  private async postJson<T>(
    payload: Record<string, unknown>,
    options: { authenticated?: boolean } = {},
  ): Promise<T> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(new Error('URL das Edge Functions não configurada.'), 'processar token');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apikey: config.supabaseAnonKey,
      Authorization: `Bearer ${config.supabaseAnonKey}`,
    };

    if (options.authenticated) {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        throw new Error('Usuária não autenticada. Faça login novamente.');
      }

      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/shared-token`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => ({} as SharedTokenEdgeResponse<T>));

    if (!response.ok || body?.success === false) {
      throw new Error(sanitizePublicLinkError(body?.error || 'Erro ao processar token.'));
    }

    if (!body.data) {
      throw new Error(body?.error || 'Resposta inválida ao processar token.');
    }

    return body.data;
  }

  async encode(
    purpose: SharedTokenPurpose | string,
    payload: Record<string, unknown>,
  ): Promise<EncodeSharedTokenResult> {
    return this.postJson<EncodeSharedTokenResult>(
      {
        action: 'encode',
        purpose,
        payload,
      },
      { authenticated: true },
    );
  }

  async decode(
    token: string,
    purpose?: SharedTokenPurpose | string,
  ): Promise<DecodeSharedTokenResult> {
    return this.postJson<DecodeSharedTokenResult>({
      action: 'decode',
      token,
      ...(purpose ? { purpose } : {}),
    });
  }

  async encodeCheckoutSuccess(
    payload: { created_at: string; data: CheckoutSuccessSnapshotData },
  ): Promise<EncodeSharedTokenResult> {
    return this.postJson<EncodeSharedTokenResult>({
      action: 'encode',
      purpose: SHARED_TOKEN_PURPOSES.CHECKOUT_SUCCESS,
      payload,
    });
  }

  async decodeCheckoutSuccess(token: string): Promise<DecodeSharedTokenResult> {
    return this.postJson<DecodeSharedTokenResult>({
      action: 'decode',
      token,
      purpose: SHARED_TOKEN_PURPOSES.CHECKOUT_SUCCESS,
    });
  }

  async encodeCheckoutAwaitingPayment(
    payload: { created_at: string; data: CheckoutSuccessSnapshotData },
  ): Promise<EncodeSharedTokenResult> {
    return this.postJson<EncodeSharedTokenResult>({
      action: 'encode',
      purpose: SHARED_TOKEN_PURPOSES.CHECKOUT_AWAITING_PAYMENT,
      payload,
    });
  }

  async decodeCheckoutAwaitingPayment(token: string): Promise<DecodeSharedTokenResult> {
    return this.postJson<DecodeSharedTokenResult>({
      action: 'decode',
      token,
      purpose: SHARED_TOKEN_PURPOSES.CHECKOUT_AWAITING_PAYMENT,
    });
  }

  async encodeCheckoutStudentUpdate(studentId: number): Promise<EncodeSharedTokenResult> {
    return this.encode(SHARED_TOKEN_PURPOSES.CHECKOUT_STUDENT_UPDATE, { studentId });
  }

  async decodeCheckoutStudentUpdate(token: string): Promise<DecodeSharedTokenResult> {
    return this.decode(token, SHARED_TOKEN_PURPOSES.CHECKOUT_STUDENT_UPDATE);
  }

  async getOrCreatePhotoUploadLink(
    studentId: number,
    linkType: 'body' | 'equipment',
  ): Promise<{
    studentId: number;
    linkType: 'body' | 'equipment';
    token: string;
    expiresAt: string;
    reusedExisting: boolean;
  }> {
    return this.postJson(
      {
        action: 'get-or-create-photo-upload-link',
        studentId,
        linkType,
      },
      { authenticated: true },
    );
  }
}

export const SharedTokenService = new SharedTokenServiceClass();
